import type { CSSProperties, ReactElement } from "react"
import type { CarriageLabelRow } from "../carriageLabelsTypes"

// A5 landscape (210 x 148 mm) group-booking carriage window label. Authored in
// mm units so svg2pdf maps 1:1 onto the page. Fonts limited to Helvetica/Times
// (the only families that survive the jsPDF + svg2pdf pipeline).
//
// Reproduces GRP003_CarriageLabels_A5_PlainPaper_ForStiffCard: a bold banner,
// the group name, route + departure time, the journey date, the booking/party
// detail and which window this label is for the seat cluster.
//
// Pure black on white — these go through the office mono printers, so no colour
// is used anywhere.

export interface CarriageLabelProps {
  row: CarriageLabelRow
  style?: CSSProperties
}

const INK = "#000000"
const W = 210
const H = 148
const INSET = 6
const TEXT_W = W - 2 * INSET - 10 // usable text width, keeping a margin inside the border

// Collapse the double-spaced "Pickering  to Whitby" the proc emits.
function tidyRoute(r: string): string {
  return (r || "").replace(/\s+/g, " ").trim()
}

// Shrink a line until it fits TEXT_W. Helvetica has no metrics available here,
// so approximate with an average advance per em and clamp to a sane floor —
// enough to stop long group names (or free-typed custom text) overrunning the
// border, which the previous fixed sizes did.
function fit(text: string, maxSize: number, advance: number, minSize = 6): number {
  const len = (text || "").length
  if (len === 0) return maxSize
  return Math.max(minSize, Math.min(maxSize, TEXT_W / (len * advance)))
}

export function CarriageLabel({ row, style }: CarriageLabelProps): ReactElement {
  const banner = (row.BannerText || "RESERVED").trim() || "RESERVED"
  const route = tidyRoute(row.OutFrom)
  const rawAccount = row.Account || ""
  const account = rawAccount.length > 60 ? rawAccount.slice(0, 57) + "..." : rawAccount
  const dateText = row.JourneyDayDate || row.JourneyDate || ""
  const labelOf =
    row.LabelNo != null && row.OfLabels != null ? `Label ${row.LabelNo} of ${row.OfLabels}` : ""
  // Booking + party size on one centred line under the date (was a 6pt line
  // wedged into the bottom-right corner, overlapping "Label X of Y").
  const detail = [
    row.Booking != null && String(row.Booking).trim() !== "" ? `Booking ${row.Booking}` : "",
    row.Qty != null ? `${row.Qty} in party` : "",
  ]
    .filter(Boolean)
    // ASCII only — the jsPDF standard-font encoding is the one thing between
    // this string and a printed label, so don't risk a middot or en dash.
    .join("   -   ")

  const bannerSize = fit(banner, 20, 0.72, 9)
  const accountSize = fit(account, 18, 0.62, 7)
  const routeSize = fit(route, 10, 0.52)
  const departsText = row.TimeOut ? `Departs ${row.TimeOut}` : ""
  const departsSize = fit(departsText, 12, 0.58)
  const dateSize = fit(dateText, 9, 0.5)
  const detailSize = fit(detail, 8, 0.5)

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      width={`${W}mm`}
      height={`${H}mm`}
      style={style}
    >
      <rect x={0} y={0} width={W} height={H} fill="#ffffff" />
      {/* outer border */}
      <rect x={INSET} y={INSET} width={W - 2 * INSET} height={H - 2 * INSET} fill="none" stroke={INK} strokeWidth={1.2} />

      {/* banner */}
      <rect x={INSET} y={INSET} width={W - 2 * INSET} height={30} fill={INK} />
      <text x={W / 2} y={28} textAnchor="middle" fontFamily="Helvetica" fontWeight="bold" fontSize={bannerSize} letterSpacing="3" fill="#ffffff">{banner}</text>

      {/* group / account name */}
      <text x={W / 2} y={66} textAnchor="middle" fontFamily="Helvetica" fontWeight="bold" fontSize={accountSize} fill={INK}>{account}</text>

      {/* route + departure time */}
      <text x={W / 2} y={88} textAnchor="middle" fontFamily="Helvetica" fontSize={routeSize} fill={INK}>{route}</text>
      <text x={W / 2} y={102} textAnchor="middle" fontFamily="Helvetica" fontWeight="bold" fontSize={departsSize} fill={INK}>{departsText}</text>

      {/* date */}
      <text x={W / 2} y={114} textAnchor="middle" fontFamily="Helvetica" fontSize={dateSize} fill={INK}>{dateText}</text>

      {/* booking + party size, centred under the date */}
      <text x={W / 2} y={124} textAnchor="middle" fontFamily="Helvetica" fontSize={detailSize} fill={INK}>{detail}</text>

      {/* which window / label */}
      <line x1={30} y1={130} x2={W - 30} y2={130} stroke={INK} strokeWidth={0.4} />
      <text x={W / 2} y={138} textAnchor="middle" fontFamily="Helvetica" fontSize={7} fill={INK}>{labelOf}</text>
    </svg>
  )
}
